import classNames from "classnames";
import React from "react";
import KuikaSmallSpinner from "../kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaSmallText from "../typography/SmallText/SmallText";
import KuikaTextMedium from "../typography/TextMedium/TextMedium";
import "./kuika-list-page.scss";

/**
 * The tool-landing page shell: an icon-led panel title, a row of "create a new X"
 * cards, a tabs/search/filters toolbar, and — below it — whatever body the panel
 * owns (usually a `KuikaListTable`).
 *
 * It is the layout counterpart of `KuikaListTable`: that component owns the rows,
 * this one owns everything around them. Process Automation and AI Functions are
 * both built from it, so the two panels cannot drift apart in padding, control
 * heights or card geometry — the values live here once.
 */

export interface KuikaListPageProps {
  children: React.ReactNode;
  /**
   * Absolute-fills the nearest positioned ancestor instead of merely filling its
   * height. Needed by hosts that size the page from a relative parent (the BPMN
   * `.modeler-parent`); hosts that already give it a fixed-height box leave it off.
   */
  fill?: boolean;
  busy?: boolean;
  /**
   * Under an editor's top bar: the page is the scrolling REST of the column rather than the
   * whole of it, so it takes the leftover height instead of claiming a second 100%.
   */
  belowBar?: boolean;
  className?: string;
  "data-testid"?: string;
}

const KuikaListPage = ({
  children,
  fill = false,
  busy,
  belowBar = false,
  className,
  "data-testid": dataTestId
}: KuikaListPageProps) => (
  <div
    className={classNames(
      "kuika-list-page",
      { "kuika-list-page--fill": fill, "kuika-list-page--below-bar": belowBar },
      className
    )}
    aria-busy={busy}
    data-testid={dataTestId}
  >
    {children}
  </div>
);

export default KuikaListPage;

//  ────────────────────────────────────────────────────────────────────────────
//  Page parts
//  ────────────────────────────────────────────────────────────────────────────

/**
 * The landing heading row. It is `KuikaPageHeader`'s `inline` variant — the same row an
 * editor wears as its top bar — kept under this name because every tool panel imports it
 * from here.
 */
export { default as KuikaListPageHeader } from "../kuika-page-header/kuika-page-header";
export type { KuikaPageHeaderProps as KuikaListPageHeaderProps } from "../kuika-page-header/kuika-page-header";

export interface KuikaCreateCardRowProps {
  children: React.ReactNode;
  className?: string;
}

/** Row of `KuikaCreateCard`s. Owns the gap and the offset from the header. */
export const KuikaCreateCardRow = ({ children, className }: KuikaCreateCardRowProps) => (
  <div className={classNames("kuika-create-card-row", className)}>{children}</div>
);

export interface KuikaCreateCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  /**
   * Per-entity tint of the icon tile. Both default to the blue action ramp; pass
   * TOKENS (`var(--purple-5)`), never raw hex, so dark mode still resolves.
   */
  iconColor?: string;
  iconBackground?: string;
  /**
   * Required unless the card is a popover's trigger (`Popover.Trigger asChild`, see `open`): the
   * trigger hands its own toggle in through this same prop, and a second one would toggle twice.
   */
  onClick?: () => void;
  disabled?: boolean;
  /** Swaps the icon for a spinner and blocks the click while creation is in flight. */
  loading?: boolean;
  className?: string;
  "data-testid"?: string;
  /** Anchor for a backend-authored onboarding tooltip step. */
  "data-onboarding-id"?: string;
  /**
   * Set by a caller that anchors a popover/menu to this card instead of opening a modal: draws a
   * chevron that flips, and the card's existing "picked" ring, while it stays `true`. Omitted (the
   * default), the card keeps its plain look — every caller that just opens a modal passes nothing.
   */
  open?: boolean;
  /** Accessible relationship to what `open` shows. Only meaningful together with `open`. */
  "aria-haspopup"?: React.AriaAttributes["aria-haspopup"];
  "aria-controls"?: string;
}

export const KuikaCreateCard = React.forwardRef<HTMLButtonElement, KuikaCreateCardProps>(
  (
    {
      title,
      description,
      icon,
      iconColor,
      iconBackground,
      onClick,
      disabled = false,
      loading = false,
      open,
      className,
      "data-testid": dataTestId,
      "data-onboarding-id": dataOnboardingId,
      "aria-haspopup": ariaHaspopup,
      "aria-controls": ariaControls
    }: KuikaCreateCardProps,
    ref: React.Ref<HTMLButtonElement>
  ) => (
    <button
      type="button"
      ref={ref}
      className={classNames("kuika-create-card", { "kuika-create-card--selected": open === true }, className)}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-haspopup={ariaHaspopup}
      aria-expanded={open}
      aria-controls={ariaControls}
      data-testid={dataTestId}
      data-onboarding-id={dataOnboardingId}
      // Computed per-card tint: the two custom properties are the card's retint hooks,
      // mirroring how KuikaListTableNameCell tints its own tile.
      style={
        {
          "--kuika-create-card-icon-bg": iconBackground,
          "--kuika-create-card-icon-color": iconColor
        } as React.CSSProperties
      }
    >
      <span className="kuika-create-card__icon">{loading ? <KuikaSmallSpinner spinning /> : icon}</span>
      <span className="kuika-create-card__text">
        <KuikaTextMedium className="kuika-create-card__title">{title}</KuikaTextMedium>
        <KuikaSmallText className="kuika-create-card__description">{description}</KuikaSmallText>
      </span>
      {open !== undefined && (
        <span className="kuika-create-card__chevron">
          <KuikaSvg type="arrows" name="chevron-down" width={16} height={16} />
        </span>
      )}
    </button>
  )
);

KuikaCreateCard.displayName = "KuikaCreateCard";

export interface KuikaListPageToolbarProps {
  /** Left slot — a `KuikaTabs`. */
  tabs?: React.ReactNode;
  /** A `KuikaInput`; the toolbar sizes it, the caller owns its placeholder/icon. */
  search?: React.ReactNode;
  /** Right-aligned filter/sort controls. Each control keeps its own width. */
  filters?: React.ReactNode;
  className?: string;
}

export const KuikaListPageToolbar = ({ tabs, search, filters, className }: KuikaListPageToolbarProps) => (
  <div className={classNames("kuika-list-page-toolbar", className)}>
    {tabs}
    {search}
    {filters && <div className="kuika-list-page-toolbar__filters">{filters}</div>}
  </div>
);
